/**
 * Simulación de la oficina: a dónde va cada avatar, cómo camina, el elevador
 * y qué luces quedan encendidas.
 *
 * Quien revisa un reporte va al cubículo de ese proyecto. Los demás pasean
 * entre lobby, sala, cafetería y baños; el destino sale del uid y del reloj,
 * así que todos los que miran la oficina los ven en el mismo lugar.
 */
import { hashId } from './pixelKit';
import { COMMON_WEIGHT } from './officeScenes';
import { elevatorStandX, elevatorWaitX, feetY, roomAt } from './officeMap';

const WALK_SPEED = 96;
const CAR_SPEED = 1.5;
const DOOR_MS = 380;
const HOLD_MS = 900;
const MUG_AFTER_MS = 3200;
const MUG_LASTS_MS = 70000;
const BUBBLES = ['dots', 'chart', 'note', 'exclaim', 'heart', 'coffee'];
const STAND_TO_MUG = { idle: 'drink', phone: 'drink', look: 'drink', stretch: 'drink', talk: 'drink' };
const SIT_TO_MUG = { sit: 'sitDrink', sitPhone: 'sitDrink', sitRead: 'sitDrink', nap: 'sitDrink' };

export function createSim() {
    return {
        actors: {},
        car: { y: 0, state: 'idle', doors: 0, timer: 0, target: null },
        levels: {},
        last: 0,
        fresh: true,
    };
}

function commonPick(map, uid, wall) {
    const base = hashId(uid);
    const period = 24000 + (base % 9) * 2600;
    const slot = Math.floor((wall + (base % period)) / period);
    const roll = hashId(`${uid}:${slot}`);
    const rooms = map.commonRooms;
    const total = rooms.reduce((sum, room) => sum + (COMMON_WEIGHT[room.kind] || 10), 0);
    let pick = roll % total;
    let chosen = rooms[0];
    for (let i = 0; i < rooms.length; i += 1) {
        const weight = COMMON_WEIGHT[rooms[i].kind] || 10;
        if (pick < weight) {
            chosen = rooms[i];
            break;
        }
        pick -= weight;
    }
    return { room: chosen, roll: roll >>> 6 };
}

function fromSpot(room, spot, roll) {
    return {
        floor: room.floor,
        x: room.x + spot.x,
        pose: spot.poses[roll % spot.poses.length],
        facing: spot.facing,
        useKey: `${room.key}:${spot.use || spot.id}`,
        hidden: Boolean(spot.hidden),
        gives: spot.gives || '',
        takes: Boolean(spot.takes),
        room,
    };
}

/** Punto suelto dentro de una sala, para cuando no hay asiento libre. */
function mill(room, uid, pose) {
    const spread = (hashId(`${uid}:${room.id}`) % 97) / 97;
    return {
        floor: room.floor,
        x: room.x + 44 + spread * Math.max(20, room.w - 130),
        pose,
        facing: hashId(uid) % 2 ? 1 : -1,
        useKey: '',
        hidden: false,
        gives: '',
        takes: false,
        room,
    };
}

function projectRoom(map, projectId) {
    if (!projectId) return null;
    for (let f = 0; f < map.floors.length; f += 1) {
        const room = map.floors[f].rooms.find((item) => item.projectId === projectId);
        if (room) return room;
    }
    return null;
}

/** Decide el destino de cada persona en este instante. */
function resolveGoals(map, roster, wall, event) {
    const goals = {};
    const taken = new Set();
    const perRoom = {};
    const people = [...roster].sort((a, b) => (a.uid < b.uid ? -1 : 1));
    const lobby = map.commonRooms.find((room) => room.kind === 'lobby') || map.commonRooms[0];
    const gather = event && event.gather ? map.commonRooms.find((room) => room.kind === event.gather) : null;

    people.forEach((person) => {
        if (person.place !== 'station') return;
        const room = projectRoom(map, person.projectId);
        if (!room) {
            const goal = mill(lobby, person.uid, 'phone');
            goal.label = 'En un reporte';
            goals[person.uid] = goal;
            return;
        }
        const order = perRoom[room.key] || 0;
        perRoom[room.key] = order + 1;
        const spot = room.work[order];
        const turn = hashId(person.uid) + Math.floor(wall / 21000);
        const goal = spot ? fromSpot(room, spot, turn) : mill(room, person.uid, 'look');
        goal.working = true;
        goal.label = person.reportName || room.name;
        goals[person.uid] = goal;
    });

    people.forEach((person) => {
        if (goals[person.uid]) return;
        if (gather) {
            goals[person.uid] = mill(gather, person.uid, event.pose || 'idle');
            return;
        }
        const { room, roll } = commonPick(map, person.uid, wall);
        let goal = null;
        for (let k = 0; k < room.spots.length && !goal; k += 1) {
            const spot = room.spots[(roll + k) % room.spots.length];
            const key = `${room.key}:${spot.id}`;
            if (!taken.has(key)) {
                taken.add(key);
                goal = fromSpot(room, spot, roll >>> 3);
            }
        }
        goals[person.uid] = goal || mill(room, person.uid, 'idle');
    });
    return goals;
}

function walkTo(actor, goalX, dt) {
    const dx = goalX - actor.x;
    if (Math.abs(dx) < 1.5) {
        actor.x = goalX;
        actor.moving = false;
        return true;
    }
    const step = Math.sign(dx) * Math.min(Math.abs(dx), (WALK_SPEED * dt) / 1000);
    actor.x += step;
    actor.dist += Math.abs(step);
    actor.facing = step >= 0 ? 1 : -1;
    actor.moving = true;
    return false;
}

function stepActor(actor, goal, map, dt, now) {
    if (actor.mode === 'ride') return;
    if (actor.floor !== goal.floor) {
        actor.useKey = '';
        actor.hidden = false;
        actor.dest = goal.floor;
        const waitX = elevatorWaitX(map) - (hashId(actor.uid) % 3) * 26;
        if (walkTo(actor, waitX, dt)) {
            actor.mode = 'wait';
            actor.facing = 1;
            actor.pose = 'idle';
        } else {
            actor.mode = 'walk';
        }
        return;
    }
    if (walkTo(actor, goal.x, dt)) {
        if (actor.mode !== 'act' || actor.goalX !== goal.x) {
            actor.since = now;
            actor.goalX = goal.x;
        }
        actor.mode = 'act';
        actor.facing = goal.facing;
        actor.pose = goal.pose;
        actor.useKey = goal.useKey;
        actor.hidden = goal.hidden;
        if (goal.gives && now - actor.since > MUG_AFTER_MS) {
            actor.carry = goal.gives;
            actor.carryUntil = now + MUG_LASTS_MS;
        }
        if (goal.takes) actor.carry = '';
    } else {
        actor.mode = 'walk';
        actor.useKey = '';
        actor.hidden = false;
    }
}

function stepCar(sim, map, list, dt, event) {
    const car = sim.car;
    const top = map.floors.length - 1;
    car.y = Math.max(0, Math.min(top, car.y));
    const here = Math.round(car.y);
    const level = Math.abs(car.y - here) < 0.001;
    const riders = () => list.filter((actor) => actor.mode === 'ride');
    const waiting = () => list.filter((actor) => actor.mode === 'wait');
    const exchange = () => {
        riders().forEach((actor) => {
            if (actor.dest !== here) return;
            actor.mode = 'walk';
            actor.floor = here;
            actor.x = elevatorWaitX(map) + 10;
            actor.facing = -1;
        });
        waiting().forEach((actor) => {
            if (actor.floor !== here || actor.dest === here) return;
            actor.mode = 'ride';
            actor.moving = false;
            actor.useKey = '';
        });
    };

    if (car.state === 'idle') {
        const wanted = level && (riders().some((actor) => actor.dest === here) || waiting().some((actor) => actor.floor === here));
        if (wanted) {
            car.state = 'opening';
        } else {
            const inside = riders();
            const stops = inside.length ? inside.map((actor) => actor.dest) : waiting().map((actor) => actor.floor);
            let target = null;
            stops.forEach((floor) => {
                if (target === null || Math.abs(floor - car.y) < Math.abs(target - car.y)) target = floor;
            });
            if (target === null && event && event.id === 'elevator') {
                const ghost = (event.seed + Math.floor(event.age / 2600)) % (top + 1);
                if (ghost !== here) target = ghost;
            }
            if (target !== null) {
                car.target = target;
                car.state = 'moving';
            }
        }
    } else if (car.state === 'moving') {
        const dir = Math.sign(car.target - car.y) || 1;
        car.y += (dir * CAR_SPEED * dt) / 1000;
        if ((car.target - car.y) * dir <= 0) {
            car.y = car.target;
            car.state = 'opening';
        }
    } else if (car.state === 'opening') {
        car.doors = Math.min(1, car.doors + dt / DOOR_MS);
        if (car.doors >= 1) {
            car.state = 'open';
            car.timer = HOLD_MS;
            exchange();
        }
    } else if (car.state === 'open') {
        exchange();
        car.timer -= dt;
        if (car.timer <= 0) car.state = 'closing';
    } else {
        car.doors = Math.max(0, car.doors - dt / DOOR_MS);
        if (car.doors <= 0) car.state = 'idle';
    }

    const standX = elevatorStandX(map);
    riders().forEach((actor, index) => {
        actor.x = standX + [0, -24, 24, -12, 12][index % 5];
        actor.rideY = car.y;
        actor.pose = 'idle';
        actor.facing = -1;
    });
}

function finalPose(actor, event) {
    if (actor.mode === 'walk') return actor.carry ? 'walkMug' : 'walk';
    if (actor.mode !== 'act') return actor.carry ? 'drink' : 'idle';
    let pose = actor.pose || 'idle';
    if (actor.carry) pose = STAND_TO_MUG[pose] || SIT_TO_MUG[pose] || pose;
    if (event && event.id === 'wave' && (pose === 'idle' || pose === 'phone' || pose === 'look' || pose === 'drink')) pose = 'wave';
    return pose;
}

/**
 * Avanza un cuadro. `now` es el reloj de animación y `wall` la hora real en ms.
 * Devuelve los actores listos para dibujar y el estado del entorno.
 */
export function stepSim(sim, map, roster, now, wall, event) {
    const dt = sim.last ? Math.max(0, Math.min(60, now - sim.last)) : 16;
    sim.last = now;
    const goals = resolveGoals(map, roster, wall, event);
    const present = new Set();
    const list = [];

    roster.forEach((person) => {
        const goal = goals[person.uid];
        if (!goal) return;
        present.add(person.uid);
        let actor = sim.actors[person.uid];
        if (!actor) {
            actor = { uid: person.uid, x: map.x0 - 90, floor: 0, mode: 'walk', facing: 1, moving: true, dist: 0, pose: 'idle', carry: '', since: now };
            if (sim.fresh) {
                actor.x = goal.x;
                actor.floor = goal.floor;
            }
            sim.actors[person.uid] = actor;
        }
        actor.person = person;
        actor.goal = goal;
        if (actor.carry && now > actor.carryUntil) actor.carry = '';
        stepActor(actor, goal, map, dt, now);
        list.push(actor);
    });
    Object.keys(sim.actors).forEach((uid) => {
        if (!present.has(uid)) delete sim.actors[uid];
    });
    sim.fresh = false;
    stepCar(sim, map, list, dt, event);

    const used = new Set();
    const groups = {};
    const actors = list.map((actor) => {
        const riding = actor.mode === 'ride';
        const room = riding ? null : roomAt(map, actor.floor, actor.x);
        const acting = actor.mode === 'act';
        if (room) used.add(`${room.key}:*`);
        if (!riding && actor.floor === 0 && actor.x > map.x0 - 50 && actor.x < map.innerX + 54) used.add(`${map.floors[0].rooms[0].key}:door`);
        if (acting && actor.useKey) used.add(actor.useKey);
        const out = {
            person: actor.person,
            x: actor.x,
            y: riding ? feetY(map, actor.rideY == null ? actor.floor : actor.rideY) : feetY(map, actor.floor),
            floor: actor.floor,
            mode: actor.mode,
            moving: actor.moving,
            facing: actor.facing,
            pose: finalPose(actor, event),
            stride: actor.dist,
            hidden: acting && actor.hidden,
            room,
            bubble: '',
            working: Boolean(actor.goal.working),
            label: riding || actor.mode === 'wait'
                ? 'Elevador'
                : actor.goal.label || (room ? room.name : 'Llegando'),
        };
        if (out.pose === 'nap') out.bubble = 'zzz';
        if (room && room.common && acting && !out.hidden && !out.working) {
            (groups[room.key] = groups[room.key] || []).push(out);
        }
        return out;
    });

    Object.keys(groups).forEach((key) => {
        const group = groups[key];
        if (group.length < 2) return;
        const turn = Math.floor(wall / 2600);
        const speaker = group[turn % group.length];
        if (speaker.pose === 'nap') return;
        speaker.bubble = BUBBLES[hashId(`${speaker.person.uid}:${turn}`) % BUBBLES.length];
        if (speaker.pose === 'idle') speaker.pose = 'talk';
        if (speaker.pose === 'sit') speaker.pose = 'sitTalk';
    });

    const blackout = event && event.id === 'blackout';
    map.floors.forEach((floor) => floor.rooms.forEach((room) => {
        const rest = room.kind === 'lobby' ? 0.75 : room.kind === 'hall' ? 0.5 : 0.12;
        const target = blackout ? 0 : used.has(`${room.key}:*`) ? 1 : rest;
        const current = sim.levels[room.key] == null ? target : sim.levels[room.key];
        const move = dt / (blackout ? 120 : 420);
        sim.levels[room.key] = current + Math.max(-move, Math.min(move, target - current));
    }));

    return { actors, used, levels: sim.levels, car: sim.car };
}
